/**
 * PROFILE, SECURITY AND SETTINGS. UF_09.
 *
 * This is where the decision to make sessions REVOCABLE (ADR 0006) finally pays for itself.
 *
 * UF_09 section 4.4 asks for "a list of active sessions and known devices with last activity", and
 * the ability to sign one out or SIGN OUT EVERYWHERE. Section 5.2 asks that a "this was not me" alert
 * actually SECURE the account.
 *
 * On a platform built with JWTs, the "sign out everywhere" button below would be a lie: it would
 * clear the local token and leave the attacker's copy working until it expired. Here it is one
 * UPDATE, and every session on the account dies before the button finishes animating.
 */

import { useConfirm } from '@/components/confirm';
import { useCallback, useEffect, useState } from 'react';
import { fmtDateTime } from '@/lib/format';
import { Link } from 'react-router-dom';
import { api, LipaApiError, type ActiveSession } from '@/lib/api';
import { TierBadge } from '@/components/chrome';
import { ErrorState, LoadingCard } from '@/components/states';
import { useSession } from '@/lib/session-context';

export default function Profile() {
  const confirm = useConfirm();
  const { me, tier, signOut } = useSession();

  const [sessions, setSessions] = useState<ActiveSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setSessions(await api.sessions());
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load your devices.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function signOutEverywhere() {
    const ok = await confirm({
      title: 'Sign out everywhere?',
      body: 'Every device signed in to this account will be signed out, including this one.',
      confirmLabel: 'Sign out everywhere',
      danger: true,
    });
    if (!ok) return;

    setBusy(true);
    try {
      await api.logoutEverywhere();
      await signOut();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-md space-y-5">
      <h1 className="text-page-title text-ink">Profile</h1>

      <section className="rounded-card border border-border bg-surface p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-ink-muted">Verification level</p>
            <p className="mt-1 font-medium text-ink">{me?.status ?? '—'}</p>
          </div>
          <TierBadge tier={tier} />
        </div>

        <p className="mt-3 truncate font-mono text-micro text-ink-muted">{me?.participant_id}</p>
      </section>

      {/* WHAT THIS TIER LETS YOU DO. The backend sends the ladder, so the app never has to guess and
          can never get out of step with the rules the platform actually enforces. */}
      {me?.tiers ? (
        <section className="rounded-card border border-border bg-surface p-5">
          <h2 className="font-medium text-ink">What you can do</h2>
          <ul className="mt-2 space-y-1 text-sm text-ink-muted">
            {me.tiers
              .find((t) => t.tier === tier)
              ?.unlocks.map((u) => (
                <li key={u} className="flex items-start gap-2">
                  <span aria-hidden className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-accent" />
                  {u}
                </li>
              ))}
          </ul>
        </section>
      ) : null}

      {/* YOUR AGREEMENTS AND SUPPORT. UF-09 sections 4.2 and 4.9: a participant can always re-read
          the disclosures they acknowledged (with versions and dates, FR-X-ID-008) and reach help. */}
      <section className="overflow-hidden rounded-card border border-border bg-surface">
        <Link
          to="/disclosures"
          className="flex items-center justify-between gap-3 px-5 py-4 transition hover:bg-canvas"
        >
          <div>
            <p className="text-sm font-medium text-ink">Disclosures and consents</p>
            <p className="mt-0.5 text-sm text-ink-muted">
              Read everything you agreed to, with its version and the date you agreed.
            </p>
          </div>
          <span aria-hidden className="text-ink-muted">
            &rsaquo;
          </span>
        </Link>
        <Link
          to="/support"
          className="flex items-center justify-between gap-3 border-t border-border px-5 py-4 transition hover:bg-canvas"
        >
          <div>
            <p className="text-sm font-medium text-ink">Support and disputes</p>
            <p className="mt-0.5 text-sm text-ink-muted">
              Raise a dispute, track a claim, or reach our team.
            </p>
          </div>
          <span aria-hidden className="text-ink-muted">
            &rsaquo;
          </span>
        </Link>
      </section>

      {/* DEVICES AND SESSIONS. */}
      <section>
        <h2 className="mb-2 font-medium text-ink">Where you are signed in</h2>

        {loading ? (
          <LoadingCard lines={2} />
        ) : error ? (
          <ErrorState body={error} onRetry={() => void load()} />
        ) : (
          <ul className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface">
            {sessions.map((s) => (
              <li key={s.session_id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-ink">{s.device_name}</p>
                  <p className="truncate text-xs text-ink-muted">
                    Last used {fmtDateTime(s.last_active_at)}
                    {s.ip_address ? ` · ${s.ip_address}` : ''}
                  </p>
                </div>

                <button
                  onClick={async () => {
                    await api.revokeSession(s.session_id);
                    await load();
                  }}
                  className="shrink-0 text-sm font-medium text-danger hover:underline"
                >
                  Sign out
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* SUPPORT. UF_07 reaches this panel from Profile, and from any transaction.

          It sits ABOVE "this was not me" on purpose: a person who has come here because something is
          wrong should find the way to tell us before they find the nuclear option. */}
      <Link
        to="/support"
        className="flex items-center justify-between rounded-card border border-border bg-surface p-5 transition hover:border-primary/40"
      >
        <div>
          <p className="font-medium text-ink">Something wrong?</p>
          <p className="mt-0.5 text-sm text-ink-muted">
            Report a problem and we will investigate it
          </p>
        </div>
        <span aria-hidden className="text-ink-muted">
          →
        </span>
      </Link>

      {/* THIS WAS NOT ME. */}
      <section className="rounded-card border border-danger/30 bg-danger/5 p-5">
        <h2 className="font-medium text-danger">See something you do not recognise?</h2>
        <p className="mt-1 text-sm text-ink-muted">
          Sign out everywhere, then change your PIN. Every device is signed out immediately,
          including whoever should not be there.
        </p>

        <button
          onClick={signOutEverywhere}
          disabled={busy}
          className="mt-4 w-full rounded-lg bg-danger py-2.5 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-50"
        >
          {busy ? 'Signing out everywhere…' : 'Sign out everywhere'}
        </button>
      </section>

      <button
        onClick={() => void signOut()}
        className="w-full rounded-lg border border-border bg-surface py-2.5 text-sm font-medium text-ink transition hover:bg-canvas"
      >
        Sign out of this device
      </button>
    </div>
  );
}
